/**
 * DegreeDataService
 * 
 * Service for loading and caching degree-related data including:
 * - Degrees from CSV and API
 * - Course lists from CSV
 * - Course data and metadata
 * - User prerequisites
 * - Progress caching with localStorage persistence
 */

import { parseDegreesCsv } from './degreeCatalogShared';
import { Degree, DegreeData, DegreeProgress, UserPrerequisiteInfo } from '@/app/types/interfaces';
import { degreesApi } from '@/app/services/api';
import { loadCatalogCourses } from '@/app/services/courseCatalogClient';
import { loadUserAcademicData, buildUserPrerequisiteInfo } from '@/app/services/userAcademicData';
import { normalize, isLikelyCourseCode } from './degreeRequirementService';
import { calculateDegreeProgress, expandCoursePools, createCalculationContext, type DegreeCalculationContext } from './degreeCalculationService';
import { clearDegreeProgressSnapshotKeys, currentDegreeProgressStorageGeneration, DEGREE_PROGRESS_CACHE_PREFIX, LEGACY_PROGRESS_KEYS } from './degreeProgressStorage';

const DEGREE_DATA_DEBUG = process.env.NEXT_PUBLIC_DEBUG_DEGREE_DATA === 'true';
let degreeCourseListsDirectPromise: Promise<DegreeCourseListsDirectMap> | null = null;
let serverDegreeCourseListsDirectPromise: Promise<DegreeCourseListsDirectMap> | null = null;
let courseDataPromise: Promise<{
  courses: any[];
  courseNameMap: Map<string, string>;
  courseCreditsMap: Map<string, number>;
}> | null = null;

function debugDegreeData(...args: unknown[]) {
  if (DEGREE_DATA_DEBUG) {
    console.debug(...args);
  }
}

function getNodeFileModules() {
  const getBuiltinModule = (process as typeof process & {
    getBuiltinModule?: (id: string) => unknown;
  }).getBuiltinModule;
  if (!getBuiltinModule) throw new Error('Node built-in module access is unavailable.');
  return {
    fs: getBuiltinModule('node:fs') as typeof import('node:fs'),
    path: getBuiltinModule('node:path') as typeof import('node:path'),
  };
}

export interface DegreeDataCache {
  staticDegrees: Map<number, Degree>;
  degreeCourseListsDirect: Map<string, { classes: string[]; name?: string; description?: string }>;
  courseNameMap: Map<string, string>;
  courseCreditsMap: Map<string, number>;
  degreeProgressCache: Map<number, DegreeProgress>;
  requirementProgressCache: Map<string, any>;
  lastUserDataHash: string;
}

export interface CachingProgress {
  current: number;
  total: number;
  phase: string;
}

export type DegreeCourseListsDirectMap = Map<string, { classes: string[]; name?: string; description?: string }>;

/**
 * Load static degrees from CSV file
 */
export async function loadStaticDegrees(options: { strict?: boolean } = {}): Promise<Map<number, Degree>> {
  try {
    const response = await fetch('/degrees.csv');
    const csvText = await response.text();

    if (!response.ok) throw new Error('Degree catalog request failed');
    const degreesCache = new Map(parseDegreesCsv(csvText, { wrappedRequirements: false }).map(degree => [degree.ID, degree]));

    debugDegreeData(`[loadStaticDegrees] Loaded ${degreesCache.size} degrees from static CSV cache`);
    return degreesCache;
  } catch (error) {
    console.error('[loadStaticDegrees] Error loading static degrees:', error);
    if (options.strict) throw error;
    return new Map();
  }
}

/**
 * Load direct course list mapping from degree_course_lists_direct.csv
 */
export async function loadDegreeCourseListsDirect(
  options: { preferServerCatalog?: boolean; strict?: boolean } = {},
): Promise<DegreeCourseListsDirectMap> {
  const serverCatalog = options.preferServerCatalog || typeof window === 'undefined';
  const cached = serverCatalog ? serverDegreeCourseListsDirectPromise : degreeCourseListsDirectPromise;
  const pending = cached ?? loadDegreeCourseListsDirectUncached(serverCatalog).catch((error) => {
    if (serverCatalog) serverDegreeCourseListsDirectPromise = null;
    else degreeCourseListsDirectPromise = null;
    throw error;
  });
  if (!cached) {
    if (serverCatalog) serverDegreeCourseListsDirectPromise = pending;
    else degreeCourseListsDirectPromise = pending;
  }
  const result = await pending;
  if (options.strict && result.size === 0) throw new Error('Degree course lists are unavailable');
  return result;
}

async function loadDegreeCourseListsDirectUncached(serverCatalog: boolean): Promise<DegreeCourseListsDirectMap> {
  try {
    let csvText: string;
    if (serverCatalog && typeof process !== 'undefined') {
      const { path, fs } = getNodeFileModules();
      const csvPath = path.join(process.cwd(), 'public', 'degree_course_lists_direct.csv');
      csvText = fs.readFileSync(csvPath, 'utf-8');
    } else {
      const response = await fetch('/degree_course_lists_direct.csv');
      csvText = await response.text();
    }

    // Parse CSV using Papa Parse
    const Papa = (await import('papaparse')).default;
    const result = Papa.parse(csvText, {
      header: true,
      dynamicTyping: false, // Keep classes as string to parse JSON
    });

    const directMap = new Map<string, { classes: string[]; name?: string; description?: string }>();
    
    result.data.forEach((row: any) => {
      const name = String(row.name || '').trim();
      const classesStr = String(row.classes || '').trim();
      const cleanName = String(row.clean_name || '').trim();
      const description = String(row.description || '').trim();
      
      if (name && classesStr) {
        try {
          // Parse JSON array string
          const classes = JSON.parse(classesStr);
          if (Array.isArray(classes)) {
            directMap.set(name, {
              classes: classes.map((c: any) => String(c).trim()),
              name: cleanName || undefined,
              description: description || undefined
            });
          }
        } catch (parseError) {
          console.warn(`[loadDegreeCourseListsDirect] Failed to parse classes for ${name}:`, parseError);
        }
      }
    });

    debugDegreeData(`[loadDegreeCourseListsDirect] Loaded ${directMap.size} direct course list mappings from CSV`);
    return directMap;
  } catch (e) {
    console.error('[loadDegreeCourseListsDirect] Error loading direct course list mappings from CSV:', e);
    return new Map();
  }
}

/**
 * Load a degree's requirements through the shared degree API/cache.
 */
export async function loadDegreeRequirementsById(degreeId: number): Promise<DegreeData | null> {
  try {
    return await degreesApi.getDegreeRequirements(degreeId);
  } catch (error) {
    console.error(`[loadDegreeRequirementsById] Error loading requirements for degree ${degreeId}:`, error);
    return null;
  }
}

/**
 * Load one degree record in both browser and server runtimes. Browser callers
 * keep the existing cached API path; server-side connectors read the checked-in
 * catalog directly so relative `/degrees.csv` fetches never escape a request.
 */
export async function loadDegreeRecordById(
  degreeId: number,
  options: { preferServerCatalog?: boolean } = {},
): Promise<Degree | null> {
  if (typeof window !== 'undefined' && !options.preferServerCatalog) return degreesApi.getById(degreeId);

  try {
    const { path, fs } = getNodeFileModules();
    const Papa = (await import('papaparse')).default;
    const csvPath = path.join(process.cwd(), 'public', 'degrees.csv');
    const parsed = Papa.parse<Record<string, string>>(fs.readFileSync(csvPath, 'utf-8'), {
      header: true,
      dynamicTyping: false,
    });
    const row = parsed.data.find((candidate) => Number(candidate.ID) === degreeId);
    if (!row) return null;
    return {
      ID: Number(row.ID),
      Name: String(row.Name || '').trim(),
      'Degree Code': String(row['Degree Code'] || '').trim(),
      'Start Term': String(row['Start Term'] || '').trim(),
      requirements: row.requirements ? JSON.parse(row.requirements) : null,
    } as Degree;
  } catch (error) {
    console.error(`[loadDegreeRecordById] Error loading degree ${degreeId}:`, error);
    return null;
  }
}

/**
 * Load degrees (from static cache or API fallback)
 */
export async function loadDegrees(staticDegreesCache: Map<number, Degree>): Promise<Degree[]> {
  try {
    debugDegreeData('[loadDegrees] Starting to load degrees...');

    // Wait a bit for static cache to load if it's still loading
    let attempts = 0;
    while (staticDegreesCache.size === 0 && attempts < 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    debugDegreeData(`[loadDegrees] Static cache has ${staticDegreesCache.size} degrees after wait`);

    // Use static cache if available, otherwise fall back to API
    if (staticDegreesCache.size > 0) {
      const allDegrees = Array.from(staticDegreesCache.values());
      debugDegreeData(`[loadDegrees] Loaded ${allDegrees.length} degrees from static cache`);
      return allDegrees;
    } else {
      const allDegrees = await degreesApi.getAll();
      debugDegreeData(`[loadDegrees] Loaded ${allDegrees.length} degrees from API`);
      return allDegrees;
    }
  } catch (error) {
    console.error('[loadDegrees] Error loading degrees:', error);
    return [];
  }
}

/**
 * Get degree by ID (from static cache or API fallback)
 */
export async function getDegreeById(
  degreeId: number,
  staticDegreesCache: Map<number, Degree>
): Promise<Degree | null> {
  // Check static cache first
  if (staticDegreesCache.has(degreeId)) {
    return staticDegreesCache.get(degreeId)!;
  }

  // Fall back to API if not in static cache
  try {
    return await degreesApi.getById(degreeId);
  } catch (error) {
    console.error(`[getDegreeById] Error loading degree ${degreeId}:`, error);
    return null;
  }
}

/**
 * Load course data and create name/credits maps
 */
export async function loadCourseData(): Promise<{
  courses: any[];
  courseNameMap: Map<string, string>;
  courseCreditsMap: Map<string, number>;
}> {
  if (courseDataPromise) return courseDataPromise;
  courseDataPromise = loadCourseDataUncached().catch((error) => {
    courseDataPromise = null;
    throw error;
  });
  return courseDataPromise;
}

async function loadCourseDataUncached(): Promise<{
  courses: any[];
  courseNameMap: Map<string, string>;
  courseCreditsMap: Map<string, number>;
}> {
  try {
    debugDegreeData('[loadCourseData] Starting to load course data...');
    const courses = await loadCatalogCourses();
    debugDegreeData(`[loadCourseData] Loaded ${courses.length} courses from catalog API`);

    const nameMap = new Map<string, string>();
    const creditsMap = new Map<string, number>();

    courses.forEach((course: any) => {
      nameMap.set(course.baseId, course.courseName);
      nameMap.set(course.id, course.courseName);
      const cleanCode = course.baseId.replace(/^0+/, '');
      nameMap.set(cleanCode, course.courseName);

      // Store credits for all course ID formats
      const credits = course.credits;
      if (credits !== null && credits !== undefined && credits !== 'TBA') {
        const numCredits = Number(credits);
        if (!isNaN(numCredits) && numCredits > 0) {
          creditsMap.set(course.baseId, numCredits);
          creditsMap.set(course.id, numCredits);
          creditsMap.set(cleanCode, numCredits);
        }
      }
    });

    debugDegreeData(`[loadCourseData] Created nameMap with ${nameMap.size} entries and creditsMap with ${creditsMap.size} entries`);
    debugDegreeData('[loadCourseData] Course data loaded successfully');
    
    return {
      courses,
      courseNameMap: nameMap,
      courseCreditsMap: creditsMap
    };
  } catch (error) {
    console.error('[loadCourseData] Error loading course data:', error);
    return {
      courses: [],
      courseNameMap: new Map(),
      courseCreditsMap: new Map()
    };
  }
}

/**
 * Load user prerequisites
 */
export async function loadUserPrerequisites(userId: string, options: { strict?: boolean } = {}): Promise<{
  prerequisites: UserPrerequisiteInfo[];
  hasCompletedCourses: boolean;
}> {
  try {
    const data = await loadUserAcademicData(userId, { fresh: options.strict });
    if (!data) {
      if (options.strict) throw new Error('Academic history is unavailable');
      return {
        prerequisites: [],
        hasCompletedCourses: false
      };
    }
    
    const normalized = await buildUserPrerequisiteInfo(data);
    const hasCompletedCourses = !!(data.completedCourses && data.completedCourses.length > 0);
    
    debugDegreeData(`[loadUserPrerequisites] Loaded ${normalized.length} user prerequisites`);
    return {
      prerequisites: normalized,
      hasCompletedCourses
    };
  } catch (e) {
    console.error('[loadUserPrerequisites] Error loading user prerequisites:', e);
    if (options.strict) throw e;
    return {
      prerequisites: [],
      hasCompletedCourses: false
    };
  }
}

const DEGREE_PROGRESS_CACHE_VERSION = '6';

type StoredDegreeProgress = {
  version: string;
  ownerId: string;
  hash: string;
  catalogKey: string;
  eligibleIds: number[];
  entries: Record<string, DegreeProgress>;
};

function progressStorageKey(ownerId: string): string {
  return `${DEGREE_PROGRESS_CACHE_PREFIX}${encodeURIComponent(ownerId)}`;
}

function removeLegacyProgressCache(): void {
  LEGACY_PROGRESS_KEYS.forEach((key) => localStorage.removeItem(key));
}

/** Bound a progress snapshot to the exact rule, pool, and credit inputs used for it. */
export async function degreeCatalogKey(
  degrees: Degree[], fullDegrees: Map<number, Degree>, lists: DegreeCourseListsDirectMap, credits: Map<string, number>,
): Promise<string> {
  const rows = [...degrees].sort((left, right) => left.ID - right.ID).map((degree) => [
    degree.ID, degree.Name, degree['Degree Code'], degree['Start Term'], fullDegrees.get(degree.ID)?.requirements ?? null,
  ]);
  const pools = [...lists].sort(([left], [right]) => left.localeCompare(right));
  const courseCredits = [...credits].sort(([left], [right]) => left.localeCompare(right));
  return sha256Json([rows, pools, courseCredits]);
}

async function sha256Json(value: unknown): Promise<string> {
  if (!globalThis.crypto?.subtle) return '';
  try {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(value)));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

/** Hash every relevant course field. If Web Crypto is unavailable, skip persistence. */
export async function generateUserDataHash(prerequisites: UserPrerequisiteInfo[]): Promise<string> {
  const rows = prerequisites.map((course) => [course.course_code, course.credits, course.grade, course.name]);
  rows.sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  return sha256Json(rows);
}

/** Only complete, owner-bound version-6 snapshots are usable. Legacy keys are discarded. */
export function loadProgressCacheFromStorage(ownerId: string, expectedHash?: string, expectedCatalogKey?: string): {
  cache: Map<number, DegreeProgress>;
  hash: string;
  complete: boolean;
} {
  const miss = { cache: new Map<number, DegreeProgress>(), hash: '', complete: false };
  if (!ownerId) return miss;
  try {
    removeLegacyProgressCache();
    const raw = localStorage.getItem(progressStorageKey(ownerId));
    if (!raw) return miss;
    const stored = JSON.parse(raw) as StoredDegreeProgress;
    if (!stored || stored.version !== DEGREE_PROGRESS_CACHE_VERSION || stored.ownerId !== ownerId
      || !stored.hash || (expectedHash !== undefined && stored.hash !== expectedHash)
      || !stored.catalogKey || (expectedCatalogKey !== undefined && stored.catalogKey !== expectedCatalogKey)
      || !Array.isArray(stored.eligibleIds) || !stored.entries || typeof stored.entries !== 'object'
      || Array.isArray(stored.entries)) return miss;
    const ids = stored.eligibleIds;
    if (ids.some((id) => !Number.isSafeInteger(id) || id <= 0) || new Set(ids).size !== ids.length
      || Object.keys(stored.entries).length !== ids.length) return miss;
    const cache = new Map<number, DegreeProgress>();
    for (const id of ids) {
      const value = stored.entries[String(id)];
      if (!value || value.degreeId !== id || !Number.isFinite(value.overallProgress)
        || !Number.isFinite(value.requirementProgress) || !Number.isFinite(value.creditProgress)) return miss;
      cache.set(id, value);
    }
    return { cache, hash: stored.hash, complete: true };
  } catch (error) {
    console.error('[loadProgressCacheFromStorage] Cache unavailable:', error);
    return miss;
  }
}

/** Persist all eligible programs or none; a pruned snapshot must never look complete. */
export function saveProgressCacheToStorage(ownerId: string, cache: Map<number, DegreeProgress>, hash: string, catalogKey: string, eligibleIds: number[], expectedGeneration = currentDegreeProgressStorageGeneration()): boolean {
  if (!ownerId || !hash || !catalogKey || expectedGeneration !== currentDegreeProgressStorageGeneration()) return false;
  const ids = [...eligibleIds].sort((a, b) => a - b);
  const complete = ids.length === cache.size && new Set(ids).size === ids.length && ids.every((id) => {
    const progress = cache.get(id);
    if (!progress) return false;
    return Number.isSafeInteger(id) && id > 0 && progress.degreeId === id
      && Number.isFinite(progress.overallProgress) && Number.isFinite(progress.requirementProgress)
      && Number.isFinite(progress.creditProgress);
  });
  try {
    removeLegacyProgressCache();
    const key = progressStorageKey(ownerId);
    if (!complete) { localStorage.removeItem(key); return false; }
    const entries: Record<string, DegreeProgress> = {};
    for (const id of ids) {
      const full = cache.get(id)!;
      entries[String(id)] = {
        degreeId: full.degreeId,
        degreeName: full.degreeName,
        overallProgress: full.overallProgress,
        requirementProgress: full.requirementProgress,
        creditProgress: full.creditProgress,
        totalCreditsNeeded: full.totalCreditsNeeded,
        totalCreditsCompleted: full.totalCreditsCompleted,
        totalCreditsPlanned: full.totalCreditsPlanned,
        estimatedCompletion: full.estimatedCompletion,
      } as DegreeProgress;
    }
    if (expectedGeneration !== currentDegreeProgressStorageGeneration()) return false;
    localStorage.setItem(key, JSON.stringify({ version: DEGREE_PROGRESS_CACHE_VERSION, ownerId, hash, catalogKey, eligibleIds: ids, entries } satisfies StoredDegreeProgress));
    return true;
  } catch (error) {
    console.error('[saveProgressCacheToStorage] Complete cache could not be saved:', error);
    try { localStorage.removeItem(progressStorageKey(ownerId)); } catch { /* Storage can be disabled. */ }
    return false;
  }
}

export function clearProgressCacheFromStorage(): void {
  clearDegreeProgressSnapshotKeys();
}

/**
 * Clear all caches (localStorage and in-memory)
 */
export function clearAllCaches(): void {
  debugDegreeData('[clearAllCaches] Clearing all degree search caches...');

  clearProgressCacheFromStorage();

  // Clear API caches via import
  import('@/app/services/api').then(({ clearDegreeSearchCaches }) => {
    clearDegreeSearchCaches();
  });

  debugDegreeData('[clearAllCaches] All caches cleared');
}

/**
 * Cache all degree progress with progress tracking
 */
export async function cacheAllDegreeProgress(
  degrees: Degree[],
  userPrerequisites: UserPrerequisiteInfo[],
  degreeCourseListsDirect: Map<string, { classes: string[]; name?: string; description?: string }>,
  staticDegreesCache: Map<number, Degree>,
  creditsMap: Map<string, number>,
  onProgress?: (progress: CachingProgress) => void,
  catalogContext?: DegreeCalculationContext['catalogCourses']
): Promise<Map<number, DegreeProgress>> {
  const newProgressCache = new Map<number, DegreeProgress>();
  const catalogCourses = catalogContext ?? await loadCatalogCourses();

  // Pre-load all course lists that will be needed across all degrees
  const allListIds = new Set<string>();
  const degreeRequirements = new Map<number, any>();

  // First pass: collect all degree requirements and list IDs
  let processedCount = 0;
  onProgress?.({
    current: 0,
    total: degrees.length,
    phase: 'Loading degree requirements...'
  });

  for (const degree of degrees) {
    try {
      const fullDegree = await getDegreeById(degree.ID, staticDegreesCache);
      const rows = Array.isArray(fullDegree?.requirements)
        ? fullDegree.requirements : fullDegree?.requirements?.requirements;
      if (fullDegree && Array.isArray(rows) && rows.length > 0) {
        degreeRequirements.set(degree.ID, fullDegree.requirements);

        const collectListIds = (reqs: any[]) => {
          if (!reqs || !Array.isArray(reqs)) return;
          for (const req of reqs) {
            if (req.requirements && Array.isArray(req.requirements)) {
              collectListIds(req.requirements);
            } else if (req.course_pool && Array.isArray(req.course_pool)) {
              for (const entry of req.course_pool) {
                // Check if entry is a course list name (not a course code)
                const normalized = normalize(String(entry));
                if (normalized.length < 6 || !/^\d+$/.test(normalized)) {
                  // Likely a course list name
                  allListIds.add(entry);
                }
              }
            }
          }
        };

        const requirementsArray = Array.isArray(fullDegree.requirements) 
          ? fullDegree.requirements 
          : (fullDegree.requirements?.requirements || []);
        collectListIds(requirementsArray);
      }
      processedCount++;
      onProgress?.({
        current: processedCount,
        total: degrees.length,
        phase: 'Loading degree requirements...'
      });
      // Awaiting cached promises only drains microtasks. Give the browser an
      // actual task boundary so a large directory cannot starve input/paint.
      if (processedCount % 20 === 0) await new Promise<void>(resolve => setTimeout(resolve, 0));
    } catch (error) {
      console.error(`[cacheAllDegreeProgress] Error pre-loading degree ${degree.ID}:`, error);
      processedCount++;
      onProgress?.({
        current: processedCount,
        total: degrees.length,
        phase: 'Loading degree requirements...'
      });
    }
  }

  // All course lists should be loaded from CSV - no database calls needed
  if (allListIds.size > 0) {
    const listNamesArray = Array.from(allListIds);
    const allListNames = listNamesArray.filter(name => degreeCourseListsDirect.has(name));

    onProgress?.({
      current: degrees.length,
      total: degrees.length,
      phase: `Loaded ${allListNames.length} course lists from direct CSV`
    });
  }

  // Second pass: calculate progress for all degrees
  onProgress?.({
    current: 0,
    total: degrees.length,
    phase: 'Calculating degree progress...'
  });

  const batchSize = 20;
  for (let i = 0; i < degrees.length; i += batchSize) {
    const batch = degrees.slice(i, i + batchSize);

    const batchPromises = batch.map(async (degree) => {
      try {
        const requirements = degreeRequirements.get(degree.ID);
        if (requirements) {
          // Expand course pools for this degree
          const expandedPools = expandCoursePools(requirements, degreeCourseListsDirect);

          // Create calculation context
          const context = createCalculationContext(
            userPrerequisites,
            expandedPools,
            creditsMap,
            {
              degreeCourseListsDirect,
              catalogCourses,
            }
          );

          // Calculate progress
          const progress = calculateDegreeProgress(
            degree,
            requirements,
            context,
            {
              // Directory rows need progress, not an optimization of every
              // program's remaining course universe. Detail owns that work.
              includeMinimumCourses: false
            }
          );
          
          newProgressCache.set(degree.ID, progress);
        }
      } catch (error) {
        console.error(`[cacheAllDegreeProgress] Error calculating progress for degree ${degree.ID}:`, error);
      }
    });

    await Promise.all(batchPromises);

    // Update progress
    const completedInThisBatch = Math.min(batchSize, degrees.length - i);
    onProgress?.({
      current: i + completedInThisBatch,
      total: degrees.length,
      phase: 'Calculating degree progress...'
    });
    if (i + batchSize < degrees.length) await new Promise<void>(resolve => setTimeout(resolve, 0));
  }

  onProgress?.({
    current: degrees.length,
    total: degrees.length,
    phase: 'Complete'
  });

  return newProgressCache;
}
